import { useMemo, useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useBulkPrint } from '../hooks/useBulkPrint'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useIsEditModeSatisfied } from '@/hooks/useIsEditModeSatisfied'
import { useTranslation } from 'react-i18next'

import { useDisclosure } from '@chakra-ui/react'

import {
  Checkbox,
  Box as DarkBox,
  SearchIcon,
  TextInput,
  Toggle,
} from '@hivewatch/dark'

import { DragEventI } from '@/components/ui'
import { CheckpointIProps } from '@/features/floor-plan'

import { DarkFloorPlanSidePanel } from '../../dark/DarkFloorPlanSidePanel'
import {
  getPlacedCheckpoints,
  getUnPlacedCheckpoints,
} from '../../utils/filterCheckpoints'
import { DarkBulkPrintModal } from './DarkBulkPrintModal'
import { DarkCheckPointListHeader } from './DarkCheckPointListHeader'
import { darkCheckPointsPanelStyles } from './DarkCheckPointsPanel.styles'
import { DarkCheckpointRow } from './DarkCheckpointRow'
import { DarkCreateCheckPointsModal } from './DarkCreateCheckPointsModal'

interface DarkCheckPointListIProps {
  colorScheme: 'dark' | 'light'
  facilityId: string
  checkpoints: CheckpointIProps[]
  isExpanded: boolean
  onToggleExpansion: () => void
  onTileDragStart: (event: DragEventI) => void
  onCheckPointClick: (checkpoint: CheckpointIProps) => void
  onDeviceItemHoverIn: (deviceId: string) => void
  onDeviceItemHoverOut: () => void
}

export const DarkCheckPointList = ({
  colorScheme,
  facilityId,
  checkpoints,
  isExpanded,
  onToggleExpansion,
  onCheckPointClick,
  onTileDragStart,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: DarkCheckPointListIProps) => {
  const { t } = useTranslation()
  // Dragging a checkpoint onto the map keeps the old Edit Mode check; every
  // other gated action uses the satisfied check (legacy CheckPointList parity).
  const isEditModeOn = useIsEditMode()
  const isEditModeSatisfied = useIsEditModeSatisfied()
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure()
  const {
    isOpen: isPrintModalOpen,
    onOpen: onPrintModalOpen,
    onClose: onPrintModalClose,
  } = useDisclosure()
  const { floorId, isFloorPlanKnownEmpty } = useFloorPlan()
  const [shouldShowPlaced, setShowPlaced] = useState(true)
  const [filter, setFilter] = useState('')

  const placedCheckpoints = useMemo(
    () => getPlacedCheckpoints(checkpoints, filter, floorId),
    [checkpoints, filter, floorId]
  )

  const unplacedCheckpoints = useMemo(
    () => getUnPlacedCheckpoints(checkpoints, filter),
    [checkpoints, filter]
  )

  // Visible checkpoints based on current filter state
  const visibleCheckpoints = useMemo(() => {
    if (shouldShowPlaced) {
      return [...unplacedCheckpoints, ...placedCheckpoints]
    }
    return unplacedCheckpoints
  }, [shouldShowPlaced, unplacedCheckpoints, placedCheckpoints])

  // Bulk print functionality - uses visible checkpoints for Select All behavior
  const {
    selectedCheckpoints,
    isSelected,
    toggleCheckpoint,
    toggleSelectAll,
    isAllSelected,
    isIndeterminate,
    selectedCount,
  } = useBulkPrint(visibleCheckpoints)

  const s = darkCheckPointsPanelStyles(colorScheme)

  const renderRow = (c: CheckpointIProps) => (
    <DarkCheckpointRow
      checkpoint={c}
      colorScheme={colorScheme}
      isEditMode={isEditModeOn}
      isSelected={isSelected(c.id)}
      key={c.id}
      onCheckPointClick={onCheckPointClick}
      onDeviceItemHoverIn={onDeviceItemHoverIn}
      onDeviceItemHoverOut={onDeviceItemHoverOut}
      onTileDragStart={onTileDragStart}
      onToggleSelect={() => toggleCheckpoint(c.id)}
    />
  )

  return (
    <DarkFloorPlanSidePanel
      dataTestid='floorPlanPage_darkCheckPointsPanel'
      header={
        <DarkCheckPointListHeader
          checkpointCount={checkpoints.length}
          colorScheme={colorScheme}
          facilityId={facilityId}
          hasFloorPlan={!isFloorPlanKnownEmpty}
          isEditModeSatisfied={isEditModeSatisfied}
          onCreateClick={onModalOpen}
          onPrintClick={onPrintModalOpen}
          selectedCount={selectedCount}
        />
      }
      isExpanded={isExpanded}
      onToggleExpansion={onToggleExpansion}
    >
      <DarkBox {...s.listBody}>
        <DarkBox {...s.filterRow}>
          <DarkBox {...s.toggleRow}>
            <Toggle
              aria-label={t('floors:checkpoint_show_unplaced')}
              colorScheme={colorScheme}
              data-testid='darkCheckpointList_unplacedToggle'
              isOn={!shouldShowPlaced}
              onChange={() => setShowPlaced(!shouldShowPlaced)}
            />
            <DarkBox as='span' {...s.toggleLabel}>
              {t('floors:checkpoint_show_unplaced')}
            </DarkBox>
          </DarkBox>
          {checkpoints.length > 0 && (
            <Checkbox
              checked={isAllSelected}
              colorScheme={colorScheme}
              data-testid='darkCheckpointList_selectAll'
              indeterminate={isIndeterminate}
              label={
                <DarkBox as='span' {...s.toggleLabel}>
                  {t('floors:checkpoint_select_all')}
                </DarkBox>
              }
              onChange={toggleSelectAll}
            />
          )}
        </DarkBox>
        <TextInput
          colorScheme={colorScheme}
          data-testid='darkCheckpointList_filter'
          leftIcon={<SearchIcon colorScheme={colorScheme} size={14} />}
          onValueChange={setFilter}
          placeholder={t(
            'floors:checkpoint_filter_by_name_or_code_placeholder'
          )}
          value={filter}
          width='100%'
        />
        {[...unplacedCheckpoints, ...placedCheckpoints].length > 0 ? (
          <>
            {shouldShowPlaced && (
              <>
                <DarkBox {...s.sectionTitle}>
                  {t('floors:checkpoint_current_floor')}
                </DarkBox>
                {placedCheckpoints.length > 0 ? (
                  <DarkBox {...s.list}>
                    {placedCheckpoints.map(renderRow)}
                  </DarkBox>
                ) : (
                  <DarkBox {...s.emptySection}>
                    {t('floors:checkpoint_no_placed')}
                  </DarkBox>
                )}
              </>
            )}
            {unplacedCheckpoints.length > 0 && (
              <>
                <DarkBox {...s.sectionTitle}>
                  {t('floors:checkpoint_unplaced_label')}
                </DarkBox>
                <DarkBox {...s.list}>
                  {unplacedCheckpoints.map(renderRow)}
                </DarkBox>
              </>
            )}
          </>
        ) : (
          <DarkBox {...s.emptyList}>
            {t('floors:checkpoint_list_empty')}
          </DarkBox>
        )}
      </DarkBox>
      {isModalOpen && (
        <DarkCreateCheckPointsModal
          isOpen={isModalOpen}
          onClose={onModalClose}
        />
      )}
      {isPrintModalOpen && (
        <DarkBulkPrintModal
          checkpoints={selectedCount > 0 ? selectedCheckpoints : checkpoints}
          facilityId={facilityId}
          isOpen={isPrintModalOpen}
          onClose={onPrintModalClose}
        />
      )}
    </DarkFloorPlanSidePanel>
  )
}
